import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tvGuideService, TVGuideQueryParams } from '@/services/tvGuideService';
import { CreateTVGuideRequest } from '@/types/model';

export const tvGuideKeys = {
  all: ['tvGuides'] as const,
  lists: () => [...tvGuideKeys.all, 'list'] as const,
  list: (params: TVGuideQueryParams) => [...tvGuideKeys.lists(), params] as const,
  details: () => [...tvGuideKeys.all, 'detail'] as const,
  detail: (id: string) => [...tvGuideKeys.details(), id] as const,
};

export const useTVGuides = (params: TVGuideQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvGuideKeys.list(params),
    queryFn: () => tvGuideService.getTVGuides(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useTVGuide = (id: string) => {
  return useQuery({
    queryKey: tvGuideKeys.detail(id),
    queryFn: () => tvGuideService.getTVGuideById(id),
    enabled: !!id,
  });
};

export const useCreateTVGuide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tvGuideData: CreateTVGuideRequest) =>
      tvGuideService.createTVGuide(tvGuideData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.lists() });
    },
  });
};

export const useUpdateTVGuide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTVGuideRequest> }) =>
      tvGuideService.updateTVGuide(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.lists() });
    },
  });
};

export const useDeleteTVGuide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvGuideService.deleteTVGuide(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.lists() });
    },
  });
};

export const useToggleTVGuideStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      tvGuideService.toggleTVGuideStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.lists() });
    },
  });
};

export const useTVGuidesForDropdown = () => {
  return useQuery({
    queryKey: ['tvGuides', 'all'],
    queryFn: () => tvGuideService.getTVGuides({ _size: 1000 }), // Get all TV guides
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => data.content, // Return just the tvGuides array
  });
};

export const useSyncTVGuide = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvGuideService.syncTVGuide(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tvGuideKeys.lists() });
    },
  });
};
